import React, { useCallback, useState } from "react";
import { LAST_STYLING_RUN_SETTING_KEY, loadDocumentSetting } from "src/services/settings-service";
import ErrorContext, { type ClassificationOutcome } from "./ErrorContext";

import type { UnavailableProfileStyle } from "src/types";

interface ErrorProviderProps extends React.PropsWithChildren {}

/**
 * Centralizes error and outcome state that was previously scattered across
 * feature providers (e.g. ProfileProvider) so consumers can read and clear
 * it independently of the business logic that produces it.
 */
export function ErrorProvider(props: ErrorProviderProps) {
  const [classificationOutcome, setClassificationOutcome] = useState<ClassificationOutcome | null>(
    null
  );
  const [classificationWarning, setClassificationWarning] = useState<string | null>(null);
  // Hydrated from the document so a failed run still blocks review after a
  // refresh. Safe at mount: React renders inside Office.onReady.
  const [lastStylingRunFailed, setLastStylingRunFailed] = useState<boolean>(
    () => loadDocumentSetting<ClassificationOutcome>(LAST_STYLING_RUN_SETTING_KEY) === "error"
  );
  const [unavailableStyles, setUnavailableStyles] = useState<UnavailableProfileStyle[] | null>(
    null
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const clearErrors = useCallback(() => {
    setClassificationOutcome(null);
    setClassificationWarning(null);
    setUnavailableStyles(null);
    setValidationError(null);
  }, []);

  const contextValue = {
    classificationOutcome,
    classificationWarning,
    lastStylingRunFailed,
    unavailableStyles,
    validationError,
    setClassificationOutcome,
    setClassificationWarning,
    setLastStylingRunFailed,
    setUnavailableStyles,
    setValidationError,
    clearErrors,
  };

  return <ErrorContext.Provider value={contextValue}>{props.children}</ErrorContext.Provider>;
}
